import axios from 'axios';
import { refreshAccessToken } from '../store/actions/AuthActions';
import { encryptData, decryptData } from '../util/cryptoUtil.js';

let isRefreshing = false;
let refreshSubscribers = [];

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  timeout: 10000,
});

// Request Interceptor
axiosInstance.interceptors.request.use((config) => {
  const { store } = require('../store/store');
  const token = store.getState()?.auth?.auth?.accessToken;

  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  if (config.data) {
    const isFormData = config.data instanceof FormData;
    if (!isFormData) {
      config.data = { payload: encryptData(JSON.stringify(config.data)) };
    }
  }

  return config;
}, (error) => Promise.reject(error));

// Helper to retry after token refresh
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    const encrypted = response.data?.payload || response.data;
    try {
      const decrypted = JSON.parse(decryptData(encrypted));
      return { data: decrypted };
    } catch (e) {
      return Promise.reject(new Error('Decryption failed'));
    }
  },

  async (error) => {
    const originalRequest = error.config;
    const encryptedError = error?.response?.data?.payload;
    let decryptedErrorData = null;
    // Try to decrypt error payload
    if (encryptedError) {
      try {
        decryptedErrorData = JSON.parse(decryptData(encryptedError));
      } catch (e) {
        console.error('Failed to decrypt error payload:', e);
      }
    }

    // Handle token expiration
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const { store } = require('../store/store');
      const state = store.getState();

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/auth/refresh-token`,
            { token: state?.auth?.auth.refreshToken },
            { withCredentials: true }
          );
          const newToken = res?.data?.data?.accessToken;
          localStorage.setItem('LNT', newToken);
          store.dispatch(refreshAccessToken(newToken));
          onRefreshed(newToken);
        } catch (err) {
          localStorage.removeItem('LNT');
          localStorage.clear();
          window.location.href = '/login';
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve) => {
        addRefreshSubscriber((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    if (decryptedErrorData) {
      console.log('decryptedErrorData', decryptedErrorData)
      return Promise.reject({ response : {data: decryptedErrorData} });
    }

    // Default fallback
    return Promise.reject(error);
  }
);

export default axiosInstance;
